import { call, put, select } from 'typed-redux-saga'
import { AnyAction } from 'redux-saga'

import services from '@services'
import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { ApplicationState } from '@store/types'
import { BridgeService } from '@services/bridge'
import { HttpStatus } from '@services/base'
import { logout } from '@utils/functions'
import { MarginErrorCodes, StepRefin } from '@utils/enums'
import { ErroType } from '@utils/types'

import { GetPropostasPayload } from '../types'
import { getPropostasError, getPropostasRequest, getPropostasSuccess } from '../actions'

export const getErrorCode = (source: unknown): string | undefined => {
  if (!source || typeof source !== 'object') return undefined
  const record = source as { errorCode?: string; data?: unknown; response?: unknown }
  if (typeof record.errorCode === 'string') return record.errorCode
  return getErrorCode(record.data) ?? getErrorCode(record.response)
}

function* showPropostaError(payload: GetPropostasPayload) {
  yield put(
    setLastServiceResponse({
      code: MarginErrorCodes.PROPOSTA_EM_ANDAMENTO,
      serviceName: 'getPropostasService',
    }),
  )
  yield put(getPropostasError())
  if (typeof payload?.errorCallback === 'function') {
    yield payload.errorCallback()
  }
  yield put(
    showError({
      title: 'Você já tem uma proposta em andamento',
      message: 'Não é possível iniciar outra operação neste momento.',
      shouldUseCustomTexts: true,
      block: true,
      code: StepRefin.ERRO_PROPOSTA_EM_ANDAMENTO,
    }),
  )
}

export default function* getPropostasSaga(action: AnyAction) {
  const payload = action.payload as GetPropostasPayload
  const { accessToken } = yield* select((state: ApplicationState) => state.session)
  try {
    const { data } = yield* call(services.getPropostasService, accessToken)

    if (getErrorCode(data) === MarginErrorCodes.PROPOSTA_EM_ANDAMENTO) {
      yield* showPropostaError(payload)
      return
    }

    yield put(setLastServiceResponse({ code: 'OK', serviceName: 'getPropostasService' }))
    yield put(getPropostasSuccess(data))

    if (typeof payload?.callback === 'function') {
      yield payload.callback(data)
    }
  } catch (error) {
    const errorCode = getErrorCode(error)
    if (errorCode === MarginErrorCodes.PROPOSTA_EM_ANDAMENTO) {
      yield* showPropostaError(payload)
      return
    }

    yield put(setLastServiceResponse({ code: 'ERROR', serviceName: 'getPropostasService' }))
    const erroTyped = error as ErroType
    const statusCode = erroTyped.status ?? erroTyped.response?.status
    yield put(getPropostasError())

    if (typeof payload?.errorCallback === 'function') {
      yield payload.errorCallback()
    }

    if (BridgeService.isBrowser() && statusCode === HttpStatus.UNAUTHORIZED) {
      yield put(
        showError({
          title: 'Sessão expirada',
          message: 'Efetue o login novamente.',
          actionTitle: 'Continuar',
          functionCallback: () => logout(),
        }),
      )
    } else {
      yield put(
        showError({
          title: 'Erro ao buscar propostas',
          message: 'Ocorreu um erro ao buscar propostas',
          actionTitle: 'Tentar Novamente',
          code: StepRefin.ERRO_BUSCAR_PROPOSTA,
          actionCallback: () => getPropostasRequest(payload),
        }),
      )
    }
  }
}
